import type { FC, ReactNode } from 'react'

import { ArrowRight, LayoutDashboard, Smartphone } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'
import GlanceDashboard from '@/components/landing/glance-dashboard'
import { siteConfig } from '@/constants/site'

const HeroSection: FC = (): ReactNode => {
    return (
        <section id='hero' aria-labelledby='hero-title' className='relative isolate overflow-hidden'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-0 -z-10'>
                <div className='absolute inset-0 bg-dot-grid [mask-image:radial-gradient(ellipse_75%_55%_at_50%_0%,black,transparent)]' />
                <div className='absolute left-1/2 top-[-14rem] h-[38rem] w-[64rem] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgb(53_102_219/0.16),transparent)]' />
            </div>

            <div className='mx-auto max-w-6xl px-5 pb-20 pt-10 sm:px-6 sm:pt-16 lg:px-8 lg:pb-28 lg:pt-20'>
                <div className='mx-auto max-w-3xl text-center'>
                    <p className='inline-flex items-center gap-2 rounded-full border border-brand-100 bg-white px-3.5 py-1.5 text-sm font-semibold text-brand-700 shadow-[0_1px_2px_rgb(20_22_33/0.05)] motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 motion-safe:duration-700'>
                        <LayoutDashboard className='size-4' />
                        ERP for growing businesses
                    </p>

                    <h1
                        id='hero-title'
                        className='mt-6 font-heading text-[2.6rem] font-extrabold leading-[1.04] tracking-[-0.035em] text-balance text-foreground sm:text-6xl lg:text-7xl motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-4 motion-safe:fill-mode-both motion-safe:duration-700 motion-safe:delay-100'
                    >
                        Your business today, <span className='text-primary'>at a glance.</span>
                    </h1>

                    <p className='mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-pretty text-muted-foreground sm:text-xl motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-4 motion-safe:fill-mode-both motion-safe:duration-700 motion-safe:delay-200'>
                        Elenix puts today&apos;s quotations, team sign-ins, low stock and security alerts on one screen, so you spend the day acting on what matters instead of chasing updates.
                    </p>

                    <div className='mt-9 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-4 motion-safe:fill-mode-both motion-safe:duration-700 motion-safe:delay-300'>
                        <a
                            href='#demo'
                            className={cn(buttonVariants({ size: 'pill-lg' }), 'shadow-[0_12px_28px_-12px_rgb(53_102_219/0.9)]')}
                        >
                            Book a demo
                            <ArrowRight className='size-4' />
                        </a>

                        <a
                            href={siteConfig.appUrl}
                            className={cn(buttonVariants({ variant: 'outline', size: 'pill-lg' }), 'bg-white')}
                        >
                            Sign in to Elenix
                        </a>
                    </div>

                    <p className='mt-5 inline-flex items-center gap-2 text-sm text-muted-foreground motion-safe:animate-in motion-safe:fade-in motion-safe:fill-mode-both motion-safe:duration-700 motion-safe:delay-500'>
                        <Smartphone className='size-4 shrink-0 text-brand-600' />
                        Your team signs in with a mobile number and PIN. No email needed.
                    </p>
                </div>

                <div className='relative mx-auto mt-14 max-w-5xl sm:mt-16 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-8 motion-safe:fill-mode-both motion-safe:duration-1000 motion-safe:delay-300'>
                    <GlanceDashboard />
                </div>
            </div>
        </section>
    )
}

export default HeroSection
